import {Injectable} from '@angular/core';
import {AppRoles} from '../app.constants';
import {MsalService} from '@azure/msal-angular';
import {environment} from '../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class RolesService {

    appRoles = AppRoles;

    constructor(private readonly authService: MsalService) {
    }

    hasRole(role: string): boolean {
        if (this.authService.instance.getAllAccounts().length === 0) {
            return false;
        }

        const accountRoles = this.getAccountRoles();
        return accountRoles.indexOf(role) > -1;
    }

    hasAnyRole(allowedRoles: string[]): boolean {
        // Mirror the backend AuthorizeRolesAttribute: a SuperAdmin satisfies any Backoffice requirement.
        const effectiveRoles = (allowedRoles.includes(this.appRoles.backoffice)
            && !allowedRoles.includes(this.appRoles.superAdmin))
            ? [...allowedRoles, this.appRoles.superAdmin]
            : allowedRoles;

        const accountRoles = this.getAccountRoles();
        return accountRoles.some(x => effectiveRoles.includes(x));
    }

    hasRoleCustomer(): boolean {
        return this.hasRole(this.appRoles.customer);
    }

    hasRoleBackoffice(): boolean {
        return this.hasRole(this.appRoles.backoffice) || this.hasRoleSuperAdmin();
    }

    hasRoleSuperAdmin(): boolean {
        return this.hasRole(this.appRoles.superAdmin);
    }

    isBackofficeAccount(): boolean {
        return this.hasRoleBackoffice();
    }

    isVisitor(): boolean {
        return !this.getAccountId();
    }

    getAccountId(): string | null {
        const accounts = this.authService.instance.getAllAccounts();

        if (accounts.length > 0) {
            const tokenClaims = accounts[0].idTokenClaims as any;

            if (tokenClaims) {
                const tokenTenantCode = tokenClaims['extension_Tenant'] as string;
                const tokenHasAccessToAllTenants = tokenClaims['extension_HasAccessToAllTenants'] as boolean;

                if (environment.tenantCode === tokenTenantCode || tokenHasAccessToAllTenants) {
                    return tokenClaims?.oid ?? null;
                }
            }
        }

        return null;
    }

    private getAccountRoles(): string[] {
        if (this.authService.instance.getAllAccounts().length > 0) {
            const token = this.authService.instance.getActiveAccount()?.idTokenClaims as any;
            if (token?.extension_Roles) {
                return token.extension_Roles.split(',');
            }
        }
        return [];
    }
}
